import { CountBadge } from '@components/CountBadge/CountBadge';
import { MultipleToggle } from '@components/MultipleToggle/MultipleToggle';
import { ModalTab } from '@lib/media/modalTab';
import classNames from 'classnames';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { visualToggleOption } from '../../../momentVisualTab';
import css from './PanelTabs.module.scss';

type Props = {
  value: ModalTab;
  onChange: (tab: ModalTab) => void;
  /** Marks the Visuals tab unavailable — a transcript-only library has no visual descriptions. */
  isTranscriptOnly: boolean;
  /**
   * How many comments the media has. Zero hides the badge rather than showing a `0` — which
   * covers "none yet" and "not read back yet" alike, and neither is worth a number.
   */
  commentCount: number;
  /**
   * How this row behaves in its host's flex axis. Required, because the two hosts disagree:
   * the mobile header is a row and wants it elastic, the desktop panel is a column and wants it
   * unshrinkable. The component cannot pick — see the note on `.scroller`.
   */
  className: string;
};

/** Below this, treat the row as fully scrolled — sub-pixel layout leaves a fraction behind. */
const SCROLL_EPSILON_PX = 1;

/**
 * The media modal's panel picker, for both of its layouts.
 *
 * It exists because the desktop header and the mobile header each built this list themselves,
 * and they had already drifted: the same tab read "Visual summary" on one and "Summary" on the
 * other. A fourth tab would have been a fourth chance to drift.
 *
 * The row scrolls sideways rather than wrapping. `MultipleToggle` is `width: fit-content` +
 * `white-space: nowrap`, so its automatic minimum equals its content width and it cannot give
 * any back — and both hosts sit inside a pane with `overflow-y: auto`, which computes
 * `overflow-x` to `auto` too, so an over-wide row would end up behind a sideways scroll of the
 * transcript. `scroll-row` is the same answer the Settings tabs reached for when their fifth tab
 * stopped fitting a phone.
 *
 * Unlike those, this row fades at whichever edge has more behind it. Settings can do without:
 * its tabs sit under a page header with nothing to their right, so a clipped pill is visibly
 * clipped. Here the row ends flush against the panel's edge, and a tab scrolled past it left no
 * trace at all — which is how Comments shipped invisible on a 1440px screen. The fade is the
 * only thing on screen saying there is more.
 */
export const PanelTabs = ({
  value,
  onChange,
  isTranscriptOnly,
  commentCount,
  className,
}: Props) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState({ start: false, end: false });

  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setOverflow({
      start: el.scrollLeft > SCROLL_EPSILON_PX,
      end: el.scrollLeft < maxScroll - SCROLL_EPSILON_PX,
    });
  }, []);

  // `GradientScrollDiv` does this same edge-fade job, and `ScenesPanel` two files over uses it
  // — it was passed over here rather than overlooked. It has since gained a `ResizeObserver`,
  // so the reason this fork still exists has narrowed to two things: its `.contentInner`
  // forces `overflow-x: scroll` at a specificity this row cannot override, which puts a
  // visible scrollbar under the pills, and its dual-wrapper sizing contract does not fit a row
  // whose flex behaviour differs per host. ijw-fyi/web.front.v2#855 tracks closing those and
  // deleting this fork.
  //
  // `ResizeObserver`, not a window listener: the panel's width is a grid track that changes when
  // the modal is resized AND when the right-hand column's `clamp()` crosses a breakpoint, and
  // only the element knows about the second one.
  //
  // The pill inside is observed too, because overflow is a comparison of the two. A row that
  // fits today stops fitting when a tab's label grows — which is exactly what the Comments
  // badge does, the first time a note is left — and the container's own size never changes
  // when that happens.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [measure]);

  // Keeps the selected tab reachable when the row is narrower than the pill: landing on the
  // Transcript tab from a stored preference should not require scrolling to find it marked.
  // `nearest` on both axes, so this never scrolls anything that is already in view — including
  // the modal behind it.
  useLayoutEffect(() => {
    scrollerRef.current
      ?.querySelector('[aria-pressed="true"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [value]);

  return (
    <div
      ref={scrollerRef}
      onScroll={measure}
      className={classNames(css.scroller, className, {
        [css.fadeStart]: overflow.start,
        [css.fadeEnd]: overflow.end,
      })}
    >
      <MultipleToggle
        className={css.toggle}
        options={[
          visualToggleOption(isTranscriptOnly),
          { value: 'scenes' as const, label: 'Scenes' },
          { value: 'transcript' as const, label: 'Transcript' },
          {
            value: 'comments' as const,
            label: 'Comments',
            // The badge is what makes the tab worth pressing: a media with notes on it says so
            // without being opened. It also widens the row, which is exactly the growth the
            // `ResizeObserver` above watches the pill for.
            // `undefined`, not `false`: `MultipleToggle` picks the label with `??`, so a
            // `false` here would render a tab with no text in it at all.
            customLabel:
              commentCount > 0 ? (
                <span className={css.tabWithCount}>
                  Comments
                  <CountBadge count={commentCount} className={css.tabCount} />
                </span>
              ) : undefined,
          },
        ]}
        value={value}
        onChange={onChange}
      />
    </div>
  );
};
